import { Injectable, inject } from '@angular/core';
import { silent403 } from '../../core/interceptors/auth.interceptor';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface RoleDto {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  user_count: number;
  permission_codes: string[];
}

export interface PermissionItemDto {
  id: string;
  code: string;
  action: string;
  resource: string;
  name: string;
}

export interface PermissionGroupDto {
  module_code: string;
  module_name: string;
  permissions: PermissionItemDto[];
}

export interface RolePayload {
  name: string;
  description: string | null;
  permission_codes: string[];
}

@Injectable({ providedIn: 'root' })
export class RolesApiService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/api/roles`;

  /**
   * Lists roles. Requires `users.roles`.
   *
   * `silentForbidden` suppresses the global "no permission" toast for callers that only need roles
   * as a nice-to-have (e.g. Help Desk notify groups) and cope without them. Without it, any role
   * lacking `users.roles` — Managers, by default — saw an error just for opening that screen.
   */
  listRoles(opts: { silentForbidden?: boolean } = {}): Promise<RoleDto[]> {
    return firstValueFrom(this.http.get<RoleDto[]>(this.base,
      opts.silentForbidden ? { context: silent403() } : {}));
  }

  listPermissions(): Promise<PermissionGroupDto[]> {
    return firstValueFrom(this.http.get<PermissionGroupDto[]>(`${this.base}/permissions`));
  }

  createRole(payload: RolePayload): Promise<{ id: string }> {
    return firstValueFrom(this.http.post<{ id: string }>(this.base, payload));
  }

  updateRole(id: string, payload: RolePayload): Promise<void> {
    return firstValueFrom(this.http.put<void>(`${this.base}/${id}`, payload));
  }

  deleteRole(id: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.base}/${id}`));
  }
}
